import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { onMounted, ref, shallowRef, watch } from 'vue';
import * as monaco from 'monaco-editor';
import dayjs from 'dayjs';
import { ProxyItem } from '@/views/management/proxy/import/ProxyTypes';
import { monacoHolder } from '@/util/monaco';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProxyForm',
  props: {
    modelValue: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const containerRef = ref<HTMLElement>();
const editRef = ref<HTMLElement>();
const editor = shallowRef<monaco.editor.IStandaloneCodeEditor>();

onMounted(() => {
  if (editRef.value) {
    const resizeObserver = new ResizeObserver(() => {
      if (editor.value) {
        editor.value.layout();
      }
    });
    resizeObserver.observe(editRef.value);
  }
});

watch([editRef], () => {
  if (editor.value) {
    editor.value.dispose();
  }
  if (!editRef.value) {
    return;
  }
  editor.value = monacoHolder.value.editor.create(editRef.value, {
    value: props.modelValue.content,
    language: 'plaintext',
    minimap: {
      enabled: false,
    },
    readOnly: false,
  });
  check();
  editor.value.onDidChangeModelContent(check);
});

function check() {
  if (!editor.value) {
    return;
  }
  const model = editor.value.getModel();
  if (!model) {
    return;
  }
  const markers: monaco.editor.IMarkerData[] = [];
  const lines = model.getLinesContent();
  const values: any[] = [];
  lines.forEach((lineContent, lineNumber) => {
    if (!/\S/.test(lineContent)) {
      return;
    }
    const res = parse(lineContent);
    if (typeof res === 'string') {
      markers.push({
        startLineNumber: lineNumber + 1,
        startColumn: 1,
        endLineNumber: lineNumber + 1,
        endColumn: lineContent.length + 1,
        message: res,
        severity: monaco.MarkerSeverity.Error,
      });
    } else {
      values.push({ index: lineNumber + 1, ...res });
    }
  });
  monacoHolder.value.editor.setModelMarkers(
    model,
    'plaintext-validation',
    markers
  );
  emit('update:modelValue', {
    content: editor.value.getValue(),
    values,
    hasError: markers.length > 0,
  });
}

const ipv4Pattern =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
const domainPattern = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
const portPattern = /^\d+$/;

function parse(lineContent: string): Omit<ProxyItem, 'index'> | string {
  const content = lineContent.replace(/\s+/g, ' ');
  let host: string,
    port: string,
    username: string,
    password: string,
    expiredTime: string | undefined;

  // 检测并解析不同格式
  if (content.includes('|')) {
    // 格式2：地址|端口|账号|密码|到期日期
    const parts = content.split('|');
    host = parts[0];
    port = parts[1];
    username = parts[2];
    password = parts[3];
    if (parts[4]) {
      expiredTime = parts[4];
    }
  } else if (content.includes('/')) {
    // 格式3和格式4：地址/端口/账号/密码，或地址/端口/账号/密码/地区
    const parts = content.split('/');
    host = parts[0];
    port = parts[1];
    username = parts[2];
    password = parts[3];
  } else {
    // 格式1：地址:端口 账号 密码
    const parts = content.split(' ');
    [host, port] = parts[0].split(':');
    username = parts[1];
    password = parts[2];
  }

  // 检查 IP 地址、域名格式
  if (
    !ipv4Pattern.test(host) &&
    !ipv6Pattern.test(host) &&
    !domainPattern.test(host)
  ) {
    return '地址格式错误';
  }

  // 检查端口格式
  if (!portPattern.test(port) || Number(port) < 1 || Number(port) > 65535) {
    return '端口格式错误';
  }

  // 检查账号、密码格式
  if (!username) {
    return '未设置账号';
  }
  if (!password) {
    return '未设置密码';
  }
  let parsedTime: string | undefined;
  if (expiredTime) {
    parsedTime = parseTime(expiredTime);
    if (!parsedTime) {
      return '过期时间格式错误';
    }
  }

  return {
    raw: content,
    host,
    port: Number(port),
    username,
    password,
    expiredTime: parsedTime,
  };
}

function parseTime(dateStr: string): string | undefined {
  // 支持的日期格式列表
  const formats = [
    'YYYY-MM-DD', // 2024-10-14
    'DD/MM/YYYY', // 14/10/2024
    'MM-DD-YYYY', // 10-14-2024
    'YYYY/MM/DD', // 2024/10/14
    'DD-MM-YYYY', // 14-10-2024
    'MM/DD/YYYY', // 10/14/2024
    'YYYY.MM.DD', // 2024.10.14
    'MMM D, YYYY', // Oct 14, 2024
    'D MMM YYYY', // 14 Oct 2024
    'YYYYMMDD', // 20241014
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm:ssZ', // 2024-10-14T12:34:56Z (ISO8601格式)
  ];
  for (const format of formats) {
    const parsedDate = dayjs(dateStr, format, true); // true 参数确保严格匹配格式
    if (parsedDate.isValid()) {
      return parsedDate.valueOf().toString(); // 返回时间戳
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "editor-container",
    style: {"border":"1px solid #d9d9d9"},
    ref_key: "containerRef",
    ref: containerRef
  }, [
    _createElementVNode("div", {
      ref_key: "editRef",
      ref: editRef,
      style: {"width":"100%","height":"380px"}
    }, null, 512)
  ], 512))
}
}

})